import React, { useCallback } from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';

import Typography from '@mui/material/Typography';
import { Transforms } from 'slate';
import { RenderElementProps, useSlate, ReactEditor } from 'slate-react';

import {
  CHIP_OUTLINE_COLOR,
  FIELD_COLOR,
  GROUPING_OPERATOR_COLOR,
  GROUPING_OPERATOR_SUGGESTIONS,
  LOGICAL_OPERATOR_COLOR,
  OPERATOR_COLOR,
  splitChipValue,
  VALUE_COLOR,
} from '../constants';
import { ChipElement as ChipElementType } from '../types';

interface ChipRenderProps extends RenderElementProps {
  element: ChipElementType;
}

export const ChipElement: React.FC<ChipRenderProps> = ({ attributes, element }) => {
  const { palette } = useTheme();
  const editor = useSlate();
  const splitValue = splitChipValue(element.value?.trim() ?? '');

  // @TODO: handle for errormap
  const isError = true;

  const handleRemove = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const path = ReactEditor.findPath(editor, element);

      Transforms.setNodes(editor, { deleteChip: true }, { at: path });
    },
    [editor, element],
  );

  const handleEdit = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const path = ReactEditor.findPath(editor, element);

      Transforms.setNodes(editor, { editChip: true }, { at: path });
    },
    [editor, element],
  );

  const tooltipTitle = isError ? '' : `TODO: error message`;

  return (
    <Tooltip title={tooltipTitle}>
      <Typography
        component="span"
        variant="body2"
        {...attributes}
        data-type="chip"
        onClick={handleEdit}
        onMouseDown={(e: React.MouseEvent) => {
          e.preventDefault();
        }}
        spellCheck={false}
        sx={{
          '&:hover .chip-remove-button': {
            opacity: 1,
          },
          alignItems: 'center',
          border: `1px solid ${isError ? CHIP_OUTLINE_COLOR : palette.error.main}`,
          borderRadius: 1.5,
          cursor: 'pointer',
          display: 'inline-block',
          maxWidth: '99%',
          mx: 0.2,
          my: 0.1,
          p: '0.2rem 0.4rem',
          position: 'relative',
          textDecoration: isError ? 'none' : 'line-through',
          verticalAlign: 'middle',
        }}
      >
        <Box
          spellCheck={false}
          sx={{
            overflow: 'hidden',
            pr: 3,
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {element.prefixOperator && (
            <Box component="span" sx={{ color: LOGICAL_OPERATOR_COLOR }}>
              {element.prefixOperator}{' '}
            </Box>
          )}
          <Box component="span" sx={{ color: FIELD_COLOR }}>
            {element.field}{' '}
          </Box>
          <Box component="span" sx={{ color: OPERATOR_COLOR }}>
            {element.operator}{' '}
          </Box>
          {splitValue.map((value, index) => {
            const isGroupingOperator =
              [...GROUPING_OPERATOR_SUGGESTIONS].includes(value) &&
              (index === 0 || index === splitValue.length - 1);
            const isPunctuation = [','].includes(value);

            return (
              <Box
                key={index}
                component="span"
                sx={{
                  color:
                    isGroupingOperator || isPunctuation ? GROUPING_OPERATOR_COLOR : VALUE_COLOR,
                }}
              >
                {value}
              </Box>
            );
          })}
        </Box>
        <IconButton
          className="chip-remove-button"
          contentEditable={false}
          onClick={handleRemove}
          onMouseDown={(e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          size="small"
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
              opacity: 1,
            },
            opacity: 0.5,
            padding: '0.1rem',
            position: 'absolute',
            right: 2,
            top: '50%',
            transform: 'translateY(-50%)',
            transition: 'opacity 0.2s',
          }}
        >
          <CancelIcon fontSize="small" sx={{ fontSize: '1.5rem' }} />
        </IconButton>
      </Typography>
    </Tooltip>
  );
};
